import {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import api, {
  refreshAccessToken,
  setAccessToken,
} from "../api/http";
import { clearLanguageOverride } from "../hooks/useBusinessLanguage";
import { clearSessionHint, setSessionHint } from "../utils/sessionHint";
import { clearSetupInProgress } from "../utils/setupFlag";
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  User,
} from "../api/types";

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<LoginResponse>;
  register: (data: RegisterRequest) => Promise<RegisterResponse>;
  logout: () => Promise<void>;
  updateUser: (updated: User) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Silent refresh on app load — try to restore session from HttpOnly cookie
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await refreshAccessToken();
        if (token && !cancelled) {
          const me = await api.get<User>("/api/v1/auth/me/");
          setUser(me);
          setSessionHint();
        } else if (!token) {
          clearSessionHint();
        }
      } catch {
        // No valid session — that's fine
        setAccessToken(null);
        clearSessionHint();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>("/api/v1/auth/login/", data);
    setAccessToken(response.access);
    // Drop any cached queries from the previous identity (a logged-out tenant
    // user's useMyBusiness, useDashboard, etc.) so the new session starts on a
    // clean slate. Without this, a fresh platform-admin login would inherit the
    // last user's business and the sidebar would show their tenant nav until
    // a manual refresh.
    queryClient.clear();
    setUser(response.user);
    setSessionHint();
    return response;
  }, [queryClient]);

  const register = useCallback(async (data: RegisterRequest): Promise<RegisterResponse> => {
    return api.post<RegisterResponse>("/api/v1/auth/register/", data);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/v1/auth/logout/");
    } catch {
      // Even if the API call fails, clear local state
    }
    setAccessToken(null);
    setUser(null);
    // Wipe React Query cache so a subsequent login (possibly as a different
    // user) doesn't see the previous user's tenant data.
    queryClient.clear();
    // Clear the user-language override so the next login picks up the new
    // business's default language instead of the prior session's choice.
    clearLanguageOverride();
    clearSessionHint();
    // Drop any mid-setup flag so a different user logging in on this tab
    // isn't pinned to the business-setup screen.
    clearSetupInProgress();
  }, [queryClient]);

  const updateUser = useCallback((updated: User) => {
    setUser(updated);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
